import * as Sentry from "@sentry/react";

const MAX_ATTRIBUTE_KEYS = 8;

function sanitizeAttributeValue(value) {
  if (value == null) {
    return undefined;
  }
  if (typeof value === "boolean") {
    return value;
  }
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : undefined;
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) {
      return undefined;
    }
    return trimmed.slice(0, 200);
  }
  return undefined;
}

export function sanitizeMetricAttributes(attributes = {}) {
  if (!attributes || typeof attributes !== "object") {
    return undefined;
  }

  const sanitized = {};
  for (const [key, value] of Object.entries(attributes)) {
    if (Object.keys(sanitized).length >= MAX_ATTRIBUTE_KEYS) {
      break;
    }
    const nextValue = sanitizeAttributeValue(value);
    if (nextValue !== undefined) {
      sanitized[String(key).slice(0, 64)] = nextValue;
    }
  }

  return Object.keys(sanitized).length > 0 ? sanitized : undefined;
}

/**
 * Thin wrapper around Sentry Application Metrics.
 * Never throws; safe to call from UI paths even if Sentry is uninitialized.
 */
export function trackAppMetric(name, attributes = {}, value = 1) {
  if (!name || typeof name !== "string") {
    return;
  }

  try {
    if (!Sentry?.metrics?.count) {
      return;
    }

    const options = {};
    const sanitizedAttributes = sanitizeMetricAttributes(attributes);
    if (sanitizedAttributes) {
      options.attributes = sanitizedAttributes;
    }

    Sentry.metrics.count(name, value, options);
  } catch (_) {
    // Metrics must never break product flows.
  }
}

export function trackAppDistribution(name, value, attributes = {}, unit) {
  if (!name || typeof name !== "string" || !Number.isFinite(value)) {
    return;
  }

  try {
    if (!Sentry?.metrics?.distribution) {
      return;
    }

    const options = {};
    const sanitizedAttributes = sanitizeMetricAttributes(attributes);
    if (sanitizedAttributes) {
      options.attributes = sanitizedAttributes;
    }
    if (unit) {
      options.unit = unit;
    }

    Sentry.metrics.distribution(name, value, options);
  } catch (_) {
    // Metrics must never break product flows.
  }
}
